import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChartModule } from 'primeng/chart';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ColorsService } from '../../services/colors.service';
import { FormatCo2eService } from 'src/app/shared/services/format-co2e.service';
import { LucideAngularModule, Calendar, Info } from 'lucide-angular';
import { TooltipModule } from 'primeng/tooltip';

interface DailyImpactData {
  date: string;
  transport: number;
  accommodation: number;
  activity: number;
  food: number;
  waste: number;
}

@Component({
  selector: 'app-impact-per-day-chart',
  standalone: true,
  imports: [CommonModule, ChartModule, TranslocoModule, LucideAngularModule, TooltipModule],
  template: `
    <div class="space-y-4">
      @if (!shouldShowEmptyState()) {
        <h4 class="text-lg font-semibold text-greyscale-900">{{ 'impact.categories.dailyImpact' | transloco }}</h4>
      }

      @if (shouldShowEmptyState()) {
        <div class="text-center py-8">
          <div class="text-greyscale-400 mb-2">
            <lucide-angular [img]="calendarIcon" class="w-8 h-8 mx-auto"></lucide-angular>
          </div>
          <p class="text-greyscale-600 text-sm">
            {{ 'impact.daily.noScheduledElements' | transloco }}
            <lucide-angular
              [img]="infoIcon"
              class="w-4 h-4 inline-block ml-1 text-greyscale-400 cursor-help"
              [pTooltip]="'impact.daily.addScheduleHint' | transloco"
              tooltipPosition="top">
            </lucide-angular>
          </p>
        </div>
      } @else {
        <div [style.height.px]="chartHeight">
          <p-chart
            type="bar"
            [data]="chartData"
            [options]="chartOptions"
            [height]="chartHeight + 'px'">
          </p-chart>
        </div>
      }
    </div>
  `,
  styles: []
})
export class ImpactPerDayChartComponent {
  @Input() dailyData: DailyImpactData[] = [];
  @Input() hasScheduledElements: boolean = true;
  @Input() showPercentage: boolean = false;

  // Icons
  calendarIcon = Calendar;
  infoIcon = Info;

  private transloco = inject(TranslocoService);
  private colorsService = inject(ColorsService);
  private formatCo2e = inject(FormatCo2eService);

  private getLocale(): string {
    const lang = this.transloco.getActiveLang?.() || 'en';
    const map: Record<string, string> = { fr: 'fr-FR', en: 'en-US', de: 'de-DE', nl: 'nl-NL' };
    return map[lang] || 'en-US';
  }

  get chartHeight(): number {
    // Pour un seul jour, utiliser une hauteur fixe raisonnable
    if (this.dailyData.length === 1) {
      return 120;
    }
    // Pour plusieurs jours, calculer dynamiquement avec une hauteur minimale
    return Math.max(300, 50 + 40 * this.dailyData.length);
  }

  get chartData() {
    if (!this.dailyData || this.dailyData.length === 0) return null;

    const labels = this.dailyData.map(d => d.date);
    
    const t = (k: string) => this.transloco.translate(k);
    const round2 = (v: number) => Math.round(v * 100) / 100;
    const datasets = [
      {
        label: t('impact.categories.transport'),
        backgroundColor: this.colorsService.TRANSPORT_COLOR,
        data: this.dailyData.map(d => this.showPercentage ? this.getPercentage(d, d.transport) : round2(d.transport)),
        maxBarThickness: 40
      },
      {
        label: t('impact.categories.accommodation'),
        backgroundColor: this.colorsService.ACCOMMODATION_COLOR,
        data: this.dailyData.map(d => this.showPercentage ? this.getPercentage(d, d.accommodation) : round2(d.accommodation)),
        maxBarThickness: 40
      },
      {
        label: t('impact.categories.activities'),
        backgroundColor: this.colorsService.ACTIVITY_COLOR,
        data: this.dailyData.map(d => this.showPercentage ? this.getPercentage(d, d.activity) : round2(d.activity)),
        maxBarThickness: 40
      },
      {
        label: t('impact.categories.food'),
        backgroundColor: this.colorsService.FOOD_COLOR,
        data: this.dailyData.map(d => this.showPercentage ? this.getPercentage(d, d.food) : round2(d.food)),
        maxBarThickness: 40
      },
      {
        label: t('impact.categories.waste'),
        backgroundColor: this.colorsService.WASTE_COLOR,
        data: this.dailyData.map(d => this.showPercentage ? this.getPercentage(d, d.waste) : round2(d.waste)),
        maxBarThickness: 40
      }
    ];

    return {
      labels,
      datasets
    };
  }

  get chartOptions() {
    const t = (k: string) => this.transloco.translate(k);
    const unitLabel = this.showPercentage ? t('impact.units.percent') : t('impact.units.kgCO2e');
    const nf = new Intl.NumberFormat(this.getLocale(), { maximumFractionDigits: 2 });
    return {
      indexAxis: 'y',
      animation: false,
      maintainAspectRatio: false,
      scales: {
        x: {
          stacked: true,
          title: { display: true, text: unitLabel, color: this.colorsService.WASTE_COLOR },
          ticks: {
            color: this.colorsService.WASTE_COLOR,
            callback: (val: string | number) => this.showPercentage ? `${Math.round(val as number)}%` : nf.format(val as number),
          },
          grid: {
            display: false
          },
          border: {
            display: false
          }
        },
        y: {
          stacked: true,
          categoryPercentage: 0.6,
          barPercentage: 0.8,
          ticks: {
            display: true,
            color: this.colorsService.WASTE_COLOR,
          },
          grid: {
            display: false
          },
          border: {
            display: false
          }
        }
      },
      plugins: {
        legend: {
          display: true,
          position: 'bottom'
        },
        tooltip: {
          mode: 'nearest',
          intersect: true,
          callbacks: {
            label: (ctx: { dataset?: { label?: string }; parsed?: { x?: number }; raw?: number }) => {
              const label = ctx.dataset?.label ? `${ctx.dataset.label}: ` : '';
              const v = ctx.parsed?.x ?? ctx.raw ?? 0;
              if (this.showPercentage) {
                return `${label}${Math.round(v)}%`;
              }
              return `${label}${this.formatCo2e.format(Math.round(v * 100) / 100)}`;
            }
          }
        }
      }
    };
  }

  private getPercentage(dayData: DailyImpactData, value: number): number {
    const total = dayData.transport + dayData.accommodation + dayData.activity + dayData.food + dayData.waste;
    return total > 0 ? Math.round((value / total) * 100) : 0;
  }

  shouldShowEmptyState(): boolean {
    return !this.hasScheduledElements || this.dailyData.length === 0;
  }
}
